import {
  Box,
  HStack,
  Icon,
  IconButton,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import { CircleMinus } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Checkbox, EmptyState, usePagination } from '@mangovoice/mango-ui'

import { useHotelsI18n } from '../../i18n'
import type { EnrichedRoom } from '../../types/rooms'
import type { WakeupCall } from '../../types/wakeup'
import {
  getHotelsPlaceholderText,
  valueOrPlaceholder,
} from '../../utils/display'
import { extensionUuidKey } from '../../utils/extensionUuid'
import { formatScheduleTime } from '../../utils/formatting'
import { TableToolbar } from '../core/TableToolbar'

interface RoomSelectionTableProps {
  rooms: EnrichedRoom[]
  wakeupCalls: WakeupCall[]
  selectedRoomIds: Set<number>
  onSelectionChange: (selectedIds: Set<number>) => void
  onDeleteWakeup: (id: number | string) => void
  isDeletingId?: number | string | null
  canStageWakeups: boolean
  canDeleteWakeups: boolean
  pbxTimeZone: string
  locale: string
}

export const RoomSelectionTable = ({
  rooms,
  wakeupCalls,
  selectedRoomIds,
  onSelectionChange,
  onDeleteWakeup,
  isDeletingId,
  canStageWakeups,
  canDeleteWakeups,
  pbxTimeZone,
  locale,
}: RoomSelectionTableProps) => {
  const { text } = useHotelsI18n()
  const placeholder = getHotelsPlaceholderText(text)
  const [searchQuery, setSearchQuery] = useState('')

  // Map room UUID -> all scheduled wake-ups (sorted by wall time in PBX TZ)
  const roomWakeupsMap = useMemo(() => {
    const map = new Map<string, WakeupCall[]>()
    for (const wc of wakeupCalls) {
      if (wc.display_status !== 'scheduled') continue
      const key = extensionUuidKey(wc.extension_uuid)
      const list = map.get(key) ?? []
      list.push(wc)
      map.set(key, list)
    }
    for (const [, list] of map) {
      list.sort((a, b) => a.wuc_timestamp.localeCompare(b.wuc_timestamp))
    }
    return map
  }, [wakeupCalls])

  const filteredRooms = useMemo(() => {
    if (!searchQuery.trim()) return rooms
    const q = searchQuery.toLowerCase().trim()
    return rooms.filter(
      (r) =>
        r.number?.toString().toLowerCase().includes(q) ||
        r.name?.toLowerCase().includes(q),
    )
  }, [rooms, searchQuery])

  const { pageSize, setPageSize, currentPage, setCurrentPage, offset, limit } =
    usePagination(filteredRooms.length, Math.max(filteredRooms.length, 1))

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, setCurrentPage])

  const paginatedRooms = useMemo(
    () => filteredRooms.slice(offset, offset + limit),
    [filteredRooms, offset, limit],
  )

  const handleToggle = useCallback(
    (roomId: number) => {
      if (!canStageWakeups) return
      const next = new Set(selectedRoomIds)
      if (next.has(roomId)) {
        next.delete(roomId)
      } else {
        next.add(roomId)
      }
      onSelectionChange(next)
    },
    [canStageWakeups, selectedRoomIds, onSelectionChange],
  )

  const handleToggleAll = useCallback(() => {
    if (!canStageWakeups) return
    if (selectedRoomIds.size === filteredRooms.length) {
      onSelectionChange(new Set())
    } else {
      onSelectionChange(new Set(filteredRooms.map((r) => r.id)))
    }
  }, [canStageWakeups, filteredRooms, selectedRoomIds, onSelectionChange])

  if (rooms.length === 0) {
    return (
      <EmptyState
        title={text(
          'hotels.wakeupScheduler.noRoomsAvailable',
          'No rooms available',
        )}
      />
    )
  }

  const allSelected =
    filteredRooms.length > 0 && selectedRoomIds.size === filteredRooms.length

  return (
    <Box
      borderWidth={1}
      borderColor="border.default"
      borderRadius="lg"
      bg="bg.default"
      overflow="hidden"
    >
      <TableToolbar
        totalCount={rooms.length}
        filteredCount={filteredRooms.length}
        currentPage={currentPage}
        pageSize={pageSize}
        searchQuery={searchQuery}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        onSearchChange={setSearchQuery}
        searchPlaceholder={text('hotels.common.search', 'Search...')}
      />

      <Box overflowY="auto" maxH="500px">
        <Table.Root size="sm" variant="line">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader
                px={4}
                py={2}
                w="40px"
                position="sticky"
                top={0}
                bg="bg.subtle"
                zIndex={1}
              >
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={handleToggleAll}
                  size="sm"
                  disabled={!canStageWakeups}
                  inputProps={{
                    'aria-label': text(
                      'hotels.wakeupScheduler.selectAllRooms',
                      'Select all rooms',
                    ),
                  }}
                />
              </Table.ColumnHeader>
              <Table.ColumnHeader
                px={4}
                py={2}
                position="sticky"
                top={0}
                bg="bg.subtle"
                zIndex={1}
              >
                {text('hotels.wakeupScheduler.roomNumber', 'Room Number')}
              </Table.ColumnHeader>
              <Table.ColumnHeader
                px={4}
                py={2}
                position="sticky"
                top={0}
                bg="bg.subtle"
                zIndex={1}
              >
                {text('hotels.wakeupScheduler.roomName', 'Room Name')}
              </Table.ColumnHeader>
              <Table.ColumnHeader
                px={4}
                py={2}
                position="sticky"
                top={0}
                bg="bg.subtle"
                zIndex={1}
              >
                {text(
                  'hotels.wakeupScheduler.currentSchedule',
                  'Current Schedule',
                )}
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {paginatedRooms.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={4} textAlign="center" py={6}>
                  <Text fontSize="sm" color="fg.muted">
                    {searchQuery
                      ? text(
                          'hotels.wakeupScheduler.noMatchingRooms',
                          'No matching rooms',
                        )
                      : text(
                          'hotels.wakeupScheduler.noRoomsAvailable',
                          'No rooms available',
                        )}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              paginatedRooms.map((room) => {
                const wakeups =
                  roomWakeupsMap.get(extensionUuidKey(room.extension_uuid)) ??
                  []
                return (
                  <Table.Row
                    key={room.uuid ?? room.id}
                    cursor={canStageWakeups ? 'pointer' : 'default'}
                    onClick={() => handleToggle(room.id)}
                    bg={
                      canStageWakeups && selectedRoomIds.has(room.id)
                        ? 'bg.emphasized'
                        : undefined
                    }
                    _hover={{ bg: 'bg.subtle' }}
                  >
                    <Table.Cell px={4} py={2}>
                      <Checkbox
                        checked={selectedRoomIds.has(room.id)}
                        onCheckedChange={() => handleToggle(room.id)}
                        onClick={(e) => e.stopPropagation()}
                        size="sm"
                        disabled={!canStageWakeups}
                        inputProps={{
                          'aria-label': text(
                            'hotels.wakeupScheduler.selectRoom',
                            'Select room {{number}}',
                            { number: room.number },
                          ),
                        }}
                      />
                    </Table.Cell>
                    <Table.Cell px={4} py={2}>
                      <Text fontSize="sm" fontWeight="medium">
                        {room.number}
                      </Text>
                    </Table.Cell>
                    <Table.Cell px={4} py={2}>
                      <Text fontSize="sm">
                        {valueOrPlaceholder(room.name, text)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell px={4} py={2}>
                      {wakeups.length > 0 ? (
                        <VStack align="stretch" gap={1}>
                          {wakeups.map((wc) => {
                            const scheduleTime = formatScheduleTime(
                              wc,
                              locale,
                              pbxTimeZone,
                            )
                            return (
                              <HStack
                                key={wc.id}
                                justify="space-between"
                                gap={2}
                              >
                                <Text fontSize="sm">{scheduleTime}</Text>
                                {canDeleteWakeups && (
                                  <Box onClick={(e) => e.stopPropagation()}>
                                    <IconButton
                                      aria-label={text(
                                        'hotels.wakeupScheduler.cancelWakeupAria',
                                        'Cancel wake-up for room {{room}} at {{time}}',
                                        {
                                          room: room.number,
                                          time: scheduleTime,
                                        },
                                      )}
                                      size="2xs"
                                      variant="subtle"
                                      borderRadius="l2"
                                      colorPalette="error"
                                      color="fg.error"
                                      _disabled={{ color: 'fg.error' }}
                                      onClick={() => onDeleteWakeup(wc.id)}
                                      loading={isDeletingId === wc.id}
                                    >
                                      <Icon
                                        as={CircleMinus}
                                        boxSize={3.5}
                                        color="fg.error"
                                      />
                                    </IconButton>
                                  </Box>
                                )}
                              </HStack>
                            )
                          })}
                        </VStack>
                      ) : (
                        <Text fontSize="sm" color="fg.muted">
                          {placeholder}
                        </Text>
                      )}
                    </Table.Cell>
                  </Table.Row>
                )
              })
            )}
          </Table.Body>
        </Table.Root>
      </Box>
    </Box>
  )
}
